import { useCallback } from 'react';

import useAccessToken from 'hooks/useAccessToken';

import type { HttpRequestOptions, UseFetchParams } from './types';

const API_HOST = import.meta.env.VITE_EXEDY_API_BASE_URL;

const DEFAULT_PARAMS: UseFetchParams = {
    apiHost: API_HOST,
    headers: { 'Content-Type': 'application/json' },
    retrieveToken: undefined
};

const useFetch = (params: UseFetchParams | undefined = undefined) => {
    const { apiHost, headers, retrieveToken } = params ?? DEFAULT_PARAMS;

    const { fetchToken } = useAccessToken();

    const fetchAccessToken: () => Promise<string | undefined> = retrieveToken ?? fetchToken;

    const resolveHost = useCallback(
        (url: string) => (url.startsWith('/') ? `${apiHost}${url}` : url),
        [apiHost]
    );

    const httpRequest = useCallback(
        async <T, D = undefined>(
            url: string,
            requestData?: D | Uint8Array,
            options: HttpRequestOptions = {},
            isBinary: boolean = false
        ) => {
            // Auto-stringify plain objects. fetch() requires a BodyInit (string,
            // Blob, FormData, URLSearchParams, etc.); a plain object gets coerced
            // via String(body) into the literal "[object Object]", which silently
            // sends garbage on the wire and forces backends to 400 with no useful
            // field paths. The historical contract was "caller stringifies", but
            // forgetting it is invisible at the call site — fix at the helper so
            // future callers can't regress. Strings, FormData, Blob, URLSearchParams,
            // ArrayBuffer, and typed-array views all pass through unchanged.
            const body =
                requestData !== null &&
                typeof requestData === 'object' &&
                !(requestData instanceof FormData) &&
                !(requestData instanceof Blob) &&
                !(requestData instanceof URLSearchParams) &&
                !(requestData instanceof ArrayBuffer) &&
                !ArrayBuffer.isView(requestData)
                    ? JSON.stringify(requestData)
                    : (requestData as BodyInit | undefined);

            const requestOptions: RequestInit = {
                body,
                cache: 'no-cache',
                redirect: 'follow',
                ...options,
                headers: new Headers({
                    ...headers,
                    ...options.headers
                })
            };

            const endpoint = resolveHost(url);
            let data: T | null = null;
            let error: string | null = null;

            try {
                const response = await fetch(endpoint, requestOptions);

                if (!response.ok) {
                    throw new Error(`HTTP Error: ${response.status}`);
                }

                if (isBinary) {
                    const body = await response.arrayBuffer();

                    data = body ? (new Uint8Array(body) as T) : null;
                } else {
                    const text = await response.text();

                    if (text) {
                        data = JSON.parse(text) as T;

                        const locationHeader = response.headers.get('location');

                        if (locationHeader && typeof data === 'object' && data !== null) {
                            (data as Record<string, unknown>).location = locationHeader;
                        }
                    }
                }
            } catch (e) {
                error = e instanceof Error ? e.message : 'Unknown error';
            }

            return { data, error };
        },
        [resolveHost, headers]
    );

    const httpGet = useCallback(
        /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
        async <T = any, D = any>(url = '', data?: D, options = {}, isBinary = false) => {
            const requestOptions = {
                ...options,
                method: 'GET'
            };

            return await httpRequest<T, D>(url, data, requestOptions, isBinary);
        },
        [httpRequest]
    );

    const httpDelete = useCallback(
        /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
        async <T = any, D = any>(url = '', data?: D, options = {}) => {
            const requestOptions = {
                ...options,
                method: 'DELETE'
            };

            return await httpRequest<T, D>(url, data, requestOptions);
        },
        [httpRequest]
    );

    const httpPost = useCallback(
        /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
        async <T = any, D = any>(url = '', data: D, options = {}, isBinary = false) => {
            const requestOptions = {
                headers: headers,

                ...options,
                method: 'POST'
            };

            return await httpRequest<T, D>(url, data, requestOptions, isBinary);
        },
        [headers, httpRequest]
    );

    const httpPut = useCallback(
        /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
        async <T = any, D = any>(url = '', data: D, options = {}) => {
            const requestOptions = {
                headers: headers,
                ...options,
                method: 'PUT'
            };

            return await httpRequest<T, D>(url, data, requestOptions);
        },
        [headers, httpRequest]
    );

    const httpPutBinary = useCallback(
        /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
        async <T = any, D = any>(url = '', data: Uint8Array, options = {}) => {
            const requestOptions = {
                ...options,
                method: 'PUT'
            };

            return await httpRequest<T, D>(url, data, requestOptions);
        },
        [httpRequest]
    );

    const httpPostFormData = useCallback(
        /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
        async <T = any>(url = '', formData: FormData, options: HttpRequestOptions = {}) => {
            const requestOptions: RequestInit = {
                body: formData,
                cache: 'no-cache',
                method: 'POST',
                redirect: 'follow',
                ...options,
                headers: new Headers({
                    ...(options.headers as Record<string, string> | undefined)
                })
            };

            const endpoint = resolveHost(url);
            let data: T | null = null;
            let error: string | null = null;

            try {
                const response = await fetch(endpoint, requestOptions);

                if (!response.ok) {
                    throw new Error(`HTTP Error: ${response.status}`);
                }

                const text = await response.text();

                if (text) {
                    data = JSON.parse(text) as T;

                    const locationHeader = response.headers.get('location');

                    if (locationHeader && typeof data === 'object' && data !== null) {
                        (data as Record<string, unknown>).location = locationHeader;
                    }
                }
            } catch (caughtError) {
                error = caughtError instanceof Error ? caughtError.message : 'Unknown error';
            }

            return { data, error };
        },
        [resolveHost]
    );

    const httpGetBinary = useCallback(async (url = '') => {
        const response = await fetch(url);
        const arrayBuffer = await response.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);

        return uint8Array;
    }, []);

    const authenticatedRequest = useCallback(
        async <T, D>(
            httpMethod: typeof httpRequest,
            url = '',
            data: D,
            options = {},
            isBinary = false
        ) => {
            const token = await fetchAccessToken();

            if (!token) {
                return {
                    data: null,
                    error: 'User not authenticated'
                };
            }

            const response = await httpMethod<T, D>(
                url,
                data,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    ...options
                },
                isBinary
            );

            if (response?.error) {
                return {
                    data: null,
                    error: response.error
                };
            }

            return response;
        },
        [fetchAccessToken]
    );

    const authenticatedGet = useCallback(
        /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
        async <T = any, D = any>(url = '', data: any = {}, options = {}) => {
            data = undefined;

            return await authenticatedRequest<T, D>(httpGet, url, data, options);
        },
        [authenticatedRequest, httpGet]
    );

    const authenticatedDelete = useCallback(
        /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
        async <T = any, D = any>(url = '', data: any = undefined, options = {}) =>
            await authenticatedRequest<T, D>(httpDelete, url, data, options),
        [authenticatedRequest, httpDelete]
    );

    const authenticatedPost = useCallback(
        /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
        async <T = any, D = any>(url = '', data: D, options = {}) =>
            await authenticatedRequest<T, D>(httpPost, url, data, options),
        [authenticatedRequest, httpPost]
    );

    const authenticatedPut = useCallback(
        /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
        async <T = any, D = any>(url = '', data: D, options = {}) =>
            await authenticatedRequest<T, D>(httpPut, url, data, options),
        [authenticatedRequest, httpPut]
    );

    const authenticatedBinaryPost = useCallback(
        /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
        async <T = any, D = any>(url = '', data: D, options = {}) =>
            await authenticatedRequest<T, D>(httpPost, url, data, options, true),
        [authenticatedRequest, httpPost]
    );

    const authenticatedPostFormData = useCallback(
        /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
        async <T = any>(url = '', formData: FormData, options: HttpRequestOptions = {}) => {
            const token = await fetchAccessToken();

            if (!token) {
                return {
                    data: null,
                    error: 'User not authenticated'
                };
            }

            return await httpPostFormData<T>(url, formData, {
                ...options,
                headers: {
                    Authorization: `Bearer ${token}`,
                    ...(options.headers as Record<string, string> | undefined)
                }
            });
        },
        [fetchAccessToken, httpPostFormData]
    );

    const authenticatedBinaryGet = useCallback(
        /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
        async <T = any, D = any>(url = '', data: any = {}, options = {}) => {
            data = undefined;

            return await authenticatedRequest<T, D>(httpGet, url, data, options, true);
        },
        [authenticatedRequest, httpGet]
    );

    return {
        authenticatedBinaryGet,
        authenticatedBinaryPost,
        authenticatedDelete,
        authenticatedGet,
        authenticatedPost,
        authenticatedPostFormData,
        authenticatedPut,
        httpDelete,
        httpGet,
        httpGetBinary,
        httpPost,
        httpPut,
        httpPutBinary,
        resolveHost
    };
};

export default useFetch;
